import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth, api } from '@/contexts/AuthContext';
import { Loader2, XCircle } from 'lucide-react';

/**
 * MagicLinkPage — /magic?t=<token>
 *
 * Consumes a one-time magic-link token → session_token → redirect.
 * Called from the link in the email we sent after the SMS fallback.
 */
export default function MagicLinkPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [state, setState] = useState('working'); // working | error
  const [errorDetail, setErrorDetail] = useState('');

  useEffect(() => {
    const token = params.get('t') || params.get('token') || '';
    if (!token) {
      setState('error');
      setErrorDetail('Link is missing a token.');
      return;
    }
    (async () => {
      try {
        const r = await api.post('/auth/magic-link/verify', { token });
        login(r.data, r.data.session_token);
        // Fresh users land on setup, returning users on discover.
        navigate(r.data.profile_complete ? '/discover' : '/profile/setup', { replace: true });
      } catch (e) {
        setState('error');
        setErrorDetail(e.response?.data?.detail || 'Could not verify this link.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6" data-testid="magic-link-page">
      <div className="w-full max-w-sm text-center">
        <div className="flex items-center gap-4 mb-8 justify-center">
          <img src="/logo-heart.png" alt="" className="w-14 h-14 object-contain" />
          <h1 className="text-[24px] font-bold tracking-tight">Meet to Keep</h1>
        </div>
        {state === 'working' && (
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-7 h-7 animate-spin text-[#B91C1C]" />
            <p className="text-sm text-zinc-500">Signing you in&hellip;</p>
          </div>
        )}
        {state === 'error' && (
          <div className="flex flex-col items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center">
              <XCircle className="w-7 h-7 text-red-600" />
            </div>
            <div>
              <p className="text-[15px] font-semibold text-[#0F0F10]">Link didn&apos;t work</p>
              <p className="text-[12.5px] text-zinc-500 mt-1">{errorDetail}</p>
            </div>
            <button
              onClick={() => navigate('/auth', { replace: true })}
              data-testid="magic-link-retry-btn"
              className="brand-gradient text-white py-3 px-6 rounded-full text-sm font-semibold"
            >
              Go to sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
